import React, { useMemo } from 'react';
import Img from 'gatsby-image';
import { graphql } from 'gatsby';
import { uid } from 'react-uid';
import { startFree } from '../../../utils/pricing';
import SendGridLink from '../../SendGridLink';
import VideoEmbed from '../../VideoEmbed';
import TrustLogoBar from '../../TrustLogoBar';
import Tag from '../../Tag';
import FacebookSvg from '../../../assets/svg/logos/facebook.svg';
import InstagramSvg from '../../../assets/svg/logos/instagram.svg';
import GoogleAdsSvg from '../../../assets/svg/logos/google-ads.svg';
import {
  StyledHero,
  StyledTitle,
  StyledCta,
  StyledImage,
  StyledVideo,
  StyledCallout,
  StyledAdsCallout,
} from './styles';

const Hero = ({ hero, children }) => {
  const heroTheme = useMemo(
    () =>
      hero.theme.toLowerCase() === 'custom color'
        ? hero.themeColor.toLowerCase().replace(/\s+/g, '-')
        : hero.theme.toLowerCase().replace(/\s+/g, '-'),
    [hero.theme]
  );

  const tagTheme = useMemo(() => {
    if (hero.theme.toLowerCase() === 'custom color')
      return hero.themeColor.toLowerCase().replace(/\s+/g, '-');

    switch (hero.theme.toLowerCase()) {
      case 'signup forms':
        return 'orange';
      case 'email design':
        return 'teal';
      case 'email marketing automation':
        return 'purple';
      default:
        return 'new';
    }
  }, [hero.theme]);

  return (
    <StyledHero className={heroTheme}>
      <div className="row row--medium-large">
        {children}

        <StyledTitle>
          <h1>
            <span dangerouslySetInnerHTML={{ __html: hero.title }} />
            {hero.badge && (
              <Tag
                dangerouslySetInnerHTML={{ __html: hero.badge }}
                color={tagTheme}
              />
            )}
          </h1>
          <p dangerouslySetInnerHTML={{ __html: hero.subtitle }} />
        </StyledTitle>

        {hero.ctas && (
          <StyledCta>
            <ul>
              {hero.ctas.map((cta, i) => {
                const classes = ['btn'];
                classes.push(i === 0 ? 'btn--primary' : 'btn--secondary');
                switch (cta.fieldGroupName.split('_').pop()) {
                  case 'TryFreeCta':
                    return (
                      <li key={uid(cta)}>
                        <button
                          type="button"
                          className={classes.join(' ')}
                          onClick={startFree}
                        >
                          {cta.text ? (
                            <span
                              dangerouslySetInnerHTML={{ __html: cta.text }}
                            />
                          ) : (
                            'Try for free'
                          )}
                        </button>
                      </li>
                    );
                  case 'CustomCta':
                    return (
                      <li key={uid(cta)}>
                        <SendGridLink
                          to={cta.url}
                          className={classes.join(' ')}
                          dangerouslySetInnerHTML={{ __html: cta.text }}
                        />
                      </li>
                    );
                  default:
                    return null;
                }
              })}
            </ul>

            {hero.includeContactSalesCta && (
              <p>
                Looking for a custom plan?{' '}
                <SendGridLink to="/contact-us-form/" className="arrow-link">
                  Contact sales
                </SendGridLink>
              </p>
            )}
          </StyledCta>
        )}

        {hero.media.map((media) => {
          switch (media.fieldGroupName.split('_').pop()) {
            case 'Video':
              return (
                <StyledVideo key={uid(media)}>
                  <VideoEmbed
                    key={media.url}
                    videoUrl={media.url}
                    poster={media.posterImage}
                  />
                </StyledVideo>
              );
            case 'Image':
              return (
                <StyledImage
                  key={uid(media)}
                  width={
                    media.file.imageFile.childImageSharp.fluid.presentationWidth
                  }
                  height={
                    media.file.imageFile.childImageSharp.fluid
                      .presentationHeight
                  }
                >
                  <Img
                    fluid={media.file.imageFile.childImageSharp.fluid}
                    alt={media.file.altText}
                  />
                </StyledImage>
              );
            default:
              return null;
          }
        })}
      </div>
      {heroTheme === 'social-and-display-ads' ? (
        <StyledAdsCallout className="row row--medium-large">
          <p>USE ADS ON THESE PLATFORMS:</p>
          <ul>
            <li>
              <FacebookSvg />
            </li>
            <li>
              <InstagramSvg />
            </li>
            <li>
              <GoogleAdsSvg />
            </li>
          </ul>
        </StyledAdsCallout>
      ) : (
        <StyledCallout>
          {hero.callout.map((callout) => {
            switch (callout.fieldGroupName.split('_').pop()) {
              case 'LogoBar':
                return (
                  <TrustLogoBar key={uid(callout)} title={callout.tagline} />
                );
              case 'Text':
                return (
                  <div className="text-callout" key={uid(callout)}>
                    <p>{callout.tagline}</p>
                    <h2>{callout.title}</h2>
                  </div>
                );
              default:
                return null;
            }
          })}
        </StyledCallout>
      )}
    </StyledHero>
  );
};

export default Hero;

export const query = graphql`
  fragment UseCaseHeroFragment on WPGraphQL_UseCase {
    acfUseCases {
      hero {
        title
        subtitle
        badge
        theme
        themeColor
        includeContactSalesCta
        ctas {
          ... on WPGraphQL_UseCase_Acfusecases_Hero_Ctas_CustomCta {
            fieldGroupName
            text
            url
          }
          ... on WPGraphQL_UseCase_Acfusecases_Hero_Ctas_TryFreeCta {
            fieldGroupName
            text
          }
        }
        media {
          ... on WPGraphQL_UseCase_Acfusecases_Hero_Media_Image {
            fieldGroupName
            file {
              sourceUrl
              altText
              imageFile {
                childImageSharp {
                  fluid(maxHeight: 368) {
                    presentationWidth
                    presentationHeight
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
          ... on WPGraphQL_UseCase_Acfusecases_Hero_Media_Video {
            fieldGroupName
            url
            posterImage {
              sourceUrl
              imageFile {
                childImageSharp {
                  fluid(maxWidth: 598) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
        callout {
          ... on WPGraphQL_UseCase_Acfusecases_Hero_Callout_Text {
            fieldGroupName
            tagline
            title
          }
          ... on WPGraphQL_UseCase_Acfusecases_Hero_Callout_LogoBar {
            fieldGroupName
            tagline
          }
        }
      }
    }
  }
`;
